import { ResponsiveBar } from "@nivo/bar";
import classNames from "classnames";
import { Fragment, useMemo } from "react";
import { OVERALL_PAGE_SUB_TYPE, PAGE_TYPE } from "../../../../types/pages.type";
import { calcProPercentsBasedOnFilterAndDays, getPositivePlusSign, itemPercentage } from "../../../../utilis/common";
import CardItemTooltip from "../../../../components/shared/CardItemTooltip";
import ColorBox from "../../../../components/shared/dashboard/ColorBox";
import NoRecordFound from "../../../../components/shared/NoRecordFound";
import CheckboxButton from "../../../../components/dashboard/shared/checkboxButton/CheckboxButton";
import styles from "./DayCardList.module.scss";
import DayCardSkeletonList from "./skeleton/DayCardSkeletonList";
import useLoadingToggle from "../../../../components/hooks/useLoadingToggle";
import { Box, Tooltip } from "@mui/material";
import CheckboxLoader from "../../../../components/shared/checkbox-loader/CheckboxLoader";
import CheckboxCircle from "../../../../components/dashboard/shared/checkboxButton/CheckboxCircle";
import usePageTypeComparison from "../../../../hooks/comparison/usePageTypeComparison";

const DayCardList = ({
	data,
	dataComparison,
	filter,
	filterComparison,
	handleToggle,
	selectedItem = [],
	type,
	page,
	averageCensusComparison,
	averageCensus,
	cardTitle,
	admissionCompareAgainst,
	admissionCompareAgainstComparison,
	reverseColorsAdmissionPage,
	loading,
	projectionDays,
	spacialSelectedItem = [],
	question,
	isCustom = false,
	isCustomTab = false,
	customTab,
	disableInteractions,
	isMobile,
	transferType,
}) => {
	const { loadingItems, handleToggleWithLoader } = useLoadingToggle();

	// Use shared comparison hook to calculate comparison data
	const { dataWithComparison } = usePageTypeComparison({
		data,
		dataComparison,
		page,
		filter,
		filterComparison,
		averageCensus,
		averageCensusComparison,
		admissionCompareAgainst,
		admissionCompareAgainstComparison,
		reverseColors: reverseColorsAdmissionPage,
		projectionDays,
		valueField: 'value',
	});

	// Map comparison data to include dayComparison alias
	const processedData = useMemo(() => {
		return dataWithComparison.map(item => ({
			...item,
			dayComparison: item.itemComparison,
		}));
	}, [dataWithComparison]);

	const defaultColor = "#FFECA6";

	const isPlusSign = useMemo(() => {
		return page === PAGE_TYPE.OVERALL && transferType === OVERALL_PAGE_SUB_TYPE.TOTAL;
	}, [page, transferType]);
	
	return (
		<div className={`w100 ${styles.dayCardList} ${isCustom && styles.custom}`}>
			<div style={{
				minWidth: isMobile ? `${processedData?.length * 50}px` : `${processedData?.length * 55}px`,
				height: isMobile ? "200px" : "245px",
				paddingTop: isMobile ? "10px" : "40px",			
				...(isMobile && {
					overflowX: "auto"					
				})	
			}}>
				{loading ? (
					<DayCardSkeletonList />
				) : (
					processedData &&
					processedData.length > 0 && (
						<ResponsiveBar
							data={[...processedData]
								.sort((a, b) => {
									return (a.isSpacialItem === true ? 1 : 0) - (b.isSpacialItem === true ? 1 : 0);
								})
								.map(item => ({
									...item,
									// Use absolute value for chart display, but keep original for calculations
									chartValue: Math.abs(item.value),
									originalValue: item.value
								}))
							}
							keys={["chartValue"]}
							indexBy="label"
							margin={{ top: 0, right: isCustom ? 15 : 0, bottom: 40, left: 0 }}
							padding={isCustom ? processedData?.length <= 2 ? 0.5 : 0.2 : 0.3}
							valueScale={{ type: "linear" }}
							indexScale={{ type: "band", round: true }}
							colors={({ data: d }) => {
								const ID = d?._id ?? null;
								return selectedItem.includes(ID) ? "#FFC700" : d?.color ?? defaultColor;
							}}
							enableArcLabels={false}
							enableArcLinkLabels={false}
							enableGridY={false}
							enableGridX={false}
							borderRadius="4"
							axisBottom={{
								tickSize: 0,
								tickPadding: 10,
								tickRotation: isMobile ? -35 : 0,
								renderTick: ({ value, x, y, opacity }) => {
									const shortLabel = value?.slice(0, 3);
									return (
										<g
											transform={`translate(${x - 16},${y + 15})`}
											style={{ opacity }}
										>
											<foreignObject width={isMobile ? 20 : 40} height={30}>
												<Tooltip title={value} placement="top">
													<div style={{ display: "flex", justifyContent: "center" }}>
														<span
															style={{
																fontSize: isMobile ? 10 : 12,
																color: "#444",
																textAlign: "center",
																pointerEvents: "auto",
															}}
														>
															{shortLabel}
														</span>
													</div>
												</Tooltip>
											</foreignObject>
										</g>
									);
								},
							}}
							label={(_label) => {
								// Use original value for percentage calculation
								const originalValue = _label.data.originalValue;
								return _label.data.percentage
									? `${_label.data.percentage}%`
									: `${itemPercentage(originalValue, null, "number", page)}%`;
							}}
							barComponent={({ bar }) => {
								return (
									<g transform={`translate(${bar.x},${bar.y})`}>
										<rect
											width={bar.width}
											height={bar.height}
											fill={bar?.data?.data?.color ?? defaultColor}
											strokeWidth="0"
											stroke={bar.data.data.color}
											focusable="false"
											rx={4}
											ry={4}
										></rect>
										{bar.data.data.percentage && (
											<text
												x={bar.width / 2}
												y={bar.height / 2}
												textAnchor="middle"
												dominantBaseline="central"
												style={{
													fontFamily: "manrope",
													fontSize: "11px",
													pointerEvents: "none",
												}}
											>
												{Math.round(bar.data.data.percentage)}%
											</text>
										)}
									</g>
								);
							}}
						/>
					)
				)}
			</div>
			<NoRecordFound data={processedData} />
			<div className={`${styles.footerWrapper}`}>
				<div className={`ffmar fs14 fw500  ${styles.itemsWrpr} ${isCustom && styles.customItemsWrpr}`}>
					{processedData?.length > 0 &&
						[...processedData]
							.sort((a, b) => {
								return (a.isSpacialItem === true ? 1 : 0) - (b.isSpacialItem === true ? 1 : 0);
							})
							.map((item, index) => {
								const ID = item._id;
								const labelId = `checkbox-list-return-label-${ID}`;
								const selected = selectedItem.indexOf(ID) !== -1;
								const itemComparison = item.dayComparison
									? { ...item.dayComparison, total: item.dayComparison.value }
									: null;
								const total = item?.value ?? item?.total ?? 0;
								
								const isSpacialItem = item?.isSpacialItem ?? false;

								return (
									<Fragment key={`${index}dayList`}>
										<div
											className={classNames("df",
												"aic",
												styles.sec,
												selected && styles.selected,
												loading && `skeleton`,
												isCustom && styles.customSec,
												processedData?.length <= 2 && styles.customSecSmall,
												processedData?.length > 2 && processedData?.length <= 4 && styles.customSecMidSmall
											)}
										>
											{loading ? null : (
												<>
													<CardItemTooltip item={item}>
														<div style={{ textAlign: "center" }} className={`${Number(total) <= 10 ? styles.valueLabel : ""}`}>
															{calcProPercentsBasedOnFilterAndDays(total, filter, projectionDays, false)}
														</div>

														<div className={`${styles.dayListCheckbox}`}>
															<Box position="relative" display="inline-flex" alignItems="center">
																{isSpacialItem ? (
																	<CheckboxCircle
																		className={styles.checkboxButton}
																		labelId={labelId}
																		handleToggle={() => {
																			if (disableInteractions) return;
																			handleToggleWithLoader(() => handleToggle({
																				item: item,
																				type,
																				itemComparison: itemComparison,
																				isChecked: spacialSelectedItem?.indexOf(ID) !== -1,
																				cardTitle,
																				isSpacialItem: item?.isSpacialItem ?? false,
																				isCustomTab,
																				customTab
																			}), ID)
																		}}
																		sx={{
																			...(loadingItems[ID] && { opacity: 0 }),
																			marginTop: "8px",
																			marginLeft: "7px",
																			textAlign: "center",
																			padding: "6px"
																		}}
																		checked={spacialSelectedItem?.indexOf(ID) !== -1 || selectedItem.indexOf(ID) !== -1}
																		disabled={disableInteractions}
																	/>
																) : (
																	<CheckboxButton
																		labelId={labelId}
																		className={`${styles.dayListLabel}`}
																		handleToggle={() =>
																			!isSpacialItem && !disableInteractions ? handleToggleWithLoader(() => handleToggle({
																				item: { ...item, total: total },
																				itemComparison,
																				type,
																				isChecked: selected,
																				cardTitle,
																				question: isCustom
																					? { isCustom: true, customQuestionInputType: question?.customQuestionInputType, isCustomTab, cardTitle, customTab }
																					: null
																			}), item?._id) : undefined
																		}
																		checked={selected}
																		sx={{
																			...(loadingItems[item?._id] && { opacity: 0 }),
																			marginTop: "2px",
																		}}
																		disabled={disableInteractions}
																	/>
																)}
																<CheckboxLoader
																	className={"small-dots-day-list"}
																	isLoading={loadingItems[item?._id]}
																/>
															</Box>
															<ColorBox
																type="button"
																color={item.comparisonColor}
																comparingAgainst={item.comparingAgainst}
																// comparingAgainstScaled={item.comparingAgainstScaled}
																comparingAgainstScaled={isPlusSign ? getPositivePlusSign(item?.comparingAgainstScaled) : item?.comparingAgainstScaled}
																numberOfDays={item.numberOfDays}
																sx={{ marginTop: "10px", marginLeft: "5px" }}
															/>
														</div>
													</CardItemTooltip>
												</>
											)}
										</div>
									</Fragment>
								);
							})}
				</div>
			</div>
		</div>
	);
};

export default DayCardList;
